/**
 * Shared HTTP timeout policy (EPIC-69, PMA-ENTITY-020).
 *
 * One policy, two mechanisms: `src/lib/api.ts` applies `DEFAULT_TIMEOUT_MS`
 * as axios's `timeout`, `src/lib/loggerApi.ts` applies the same values via
 * `AbortSignal.timeout()` on every `fetch`. A caller on either mechanism
 * that needs the long value passes it through explicitly (axios: a
 * per-call `{ timeout: LONG_TIMEOUT_MS }` config; fetch: the third
 * `timeoutMs` argument to `fetchLoggerWithAuth`) rather than picking its
 * own number.
 *
 * DEFAULT_TIMEOUT_MS (15s) — every ordinary CRUD/read call:
 *  - Cloud Run scales to zero in both environments
 *    (ProMatchAnalytics-Backend/infra/environment/locals.tf: `min_instances
 *    = 0` for both dev and prod), so the first request after an idle
 *    period pays a real cold start (container boot + Motor/Mongo
 *    connection warmup) on top of query time, not query time alone.
 *  - Everything this default covers is a single-document Mongo operation
 *    (`find_one` / `findOneAndUpdate` / `insert_one`) or a single
 *    page-size-capped paginated read — sub-second once the container is
 *    warm.
 *  - Ceiling: Cloud Run's own request timeout is 300s
 *    (ProMatchAnalytics-Backend/infra/environment/cloud_run.tf:16). A
 *    client timeout far below that means the client's own timeout fires
 *    first and the user gets an actionable rejection, instead of the
 *    platform silently dropping the connection with no signal at all.
 *
 * LONG_TIMEOUT_MS (45s) — reserved for a path verified by reading the
 * backend handler (not assumed from its name) to do real work beyond a
 * single small Mongo op:
 *  - AI narrative / query / chat (`generateNarrative`, `postAiQuery`,
 *    `postAiChat` in loggerApi.ts): the backend's own upstream LLM
 *    provider call has a 30s timeout
 *    (ProMatchAnalytics-Backend/app/services/ai_providers/{gemini,openrouter}.py:
 *    `TIMEOUT = 30.0`). 45s gives margin above that so the frontend
 *    timeout never races ahead of a backend call that is still
 *    legitimately in flight.
 *  - Media uploads (`uploadTeamLogo`, `uploadPlayerPhoto` in
 *    loggerApi.ts): up to 5MB of client -> backend transfer plus a
 *    backend -> GCS hop, both over whatever connection the operator has
 *    pitchside.
 *  - Per-match player-stats aggregation (`fetchMatchPlayerStats` in
 *    playerStats.ts, which also backs the CSV/PDF stats export in
 *    export.ts): the backend loads every event for the match with an
 *    unbounded `.to_list(length=None)` and runs two synchronous passes
 *    (`compute_player_stats`, `compute_player_ratings`) before responding
 *    (ProMatchAnalytics-Backend/app/routers/dashboard.py:149-157) — the
 *    one read path here whose cost is proportional to match size rather
 *    than a fixed small op.
 *  - `createBulkIngestion` and `retryFailedItems` (ingestion.ts): unlike
 *    `createBatch`/`createBatchIngestion`, which do one `insert_one` and
 *    hand every per-row cost off to `BackgroundTasks`
 *    (ProMatchAnalytics-Backend/app/routers/ingestions.py:300-350), these
 *    two run synchronously in-request — bulk parses and validates the
 *    whole upload before responding, and retry re-runs
 *    dedup/similarity-matching for every failed item with no
 *    `BackgroundTasks` hand-off
 *    (ProMatchAnalytics-Backend/app/routers/ingestions.py:884-966,
 *    626-644).
 *
 * Everything else on both mechanisms — including `fetchAdvancedMetrics`
 * (reads a precomputed doc,
 * ProMatchAnalytics-Backend/app/routers/matches_crud.py:1112-1154) and
 * `createBatch`/`createBatchIngestion` (a single `insert_one`, all
 * per-row work backgrounded) — was verified against its backend handler
 * and fits inside `DEFAULT_TIMEOUT_MS`; see the per-call comments in
 * `api.ts` and `loggerApi.ts` call sites for the individual citations.
 *
 * E2E override (EPIC-69 AC3/AC4): `LD-stall-is-constructed-not-awaited`
 * requires the *stall* to be deterministic (a route handler that never
 * fulfils), but a test that then waits out the real 15s/45s to observe the
 * rejection is slow and proves nothing the mechanism itself doesn't
 * already guarantee. A spec that needs to observe the rejection/recovery
 * path without the real wait sets `window.__E2E_HTTP_TIMEOUT_OVERRIDE_MS__`
 * via `page.addInitScript()` before navigating — read once, here, at
 * module load, so it applies to axios's construction-time `timeout` option
 * exactly like every other consumer of these two constants. Gated
 * three ways so it can never leak into a real deployment or affect a
 * spec that doesn't opt in: `IS_E2E_TEST_MODE` (already gates the E2E
 * auth-bypass path in both `api.ts` and `loggerApi.ts`), a `window`
 * existence check (this module also loads in vitest's node/jsdom
 * environment, which may not define it identically), and a positive-number
 * check on the override value itself. No spec sets this global today
 * except the one this epic adds (`e2e/admin-teams-stalled-request-timeout.spec.ts`);
 * every other spec — including EPIC-66's own
 * `e2e/admin-teams-response-race.spec.ts`, which relies on the real
 * default staying large enough to never fire against its 700ms artificial
 * delay — reads `window.__E2E_HTTP_TIMEOUT_OVERRIDE_MS__` as `undefined`
 * and gets the unmodified production value.
 */
const IS_E2E_TEST_MODE = import.meta.env.VITE_E2E_TEST_MODE === "true";

function resolveTimeoutMs(productionValueMs: number): number {
  if (!IS_E2E_TEST_MODE) return productionValueMs;
  if (typeof window === "undefined") return productionValueMs;
  const override = (
    window as unknown as { __E2E_HTTP_TIMEOUT_OVERRIDE_MS__?: unknown }
  ).__E2E_HTTP_TIMEOUT_OVERRIDE_MS__;
  return typeof override === "number" && override > 0
    ? override
    : productionValueMs;
}

export const DEFAULT_TIMEOUT_MS = resolveTimeoutMs(15_000);
export const LONG_TIMEOUT_MS = resolveTimeoutMs(45_000);
